import { DatadogProvider } from '@datadog/openfeature-browser';
import { FlagdWebProvider } from '@openfeature/flagd-web-provider';
import type { Provider } from '@openfeature/web-sdk';
import { z } from 'zod';

import { WebInMemoryFeatureFlagProvider } from './in-memory-feature-flag-provider';

export const webProviderConfigSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('flagd'),
    host: z.string().min(1),
    port: z.coerce.number().int().positive(),
    tls: z.boolean().default(false),
  }),
  z.object({
    kind: z.literal('datadog'),
    clientToken: z.string().min(1),
    applicationId: z.string().min(1).optional(),
    site: z.string().min(1),
    env: z.string().min(1),
    service: z.string().min(1).optional(),
    version: z.string().min(1).optional(),
    flaggingProxy: z.string().min(1).optional(),
  }),
  z.object({ kind: z.literal('in-memory') }),
]);

export type WebProviderConfig = z.infer<typeof webProviderConfigSchema>;

/**
 * Same-origin path serving the Datadog flag-configuration fetch through our own
 * CloudFront distribution (ENG-676).
 *
 * The SDK would otherwise fetch from `preview.ff-cdn.datadoghq.eu`, which
 * EasyPrivacy blocks via `||datadoghq.eu^$third-party` — and because provider
 * failure is non-fatal, a blocked user silently resolves every flag to its
 * registry default instead of their org's targeting. Serving it first-party
 * takes it out of the `$third-party` rules' scope.
 *
 * Must stay in sync with `DATADOG_FLAGS_PROXY_VIEWER_PATH` in
 * `cdk-stack/lib/datadogFlagProxy.ts`, which is what CloudFront accepts. Both
 * sides are locked by tests.
 */
export const DATADOG_FLAGGING_PROXY_PATH = '/_dd/precompute-assignments';

/**
 * Builds the OpenFeature provider for the web (browser) runtime.
 *
 * - `flagd`   — streams evaluations from the local flagd sidecar over
 *               gRPC-web; flag changes push to the browser without a reload.
 * - `datadog` — evaluates against Datadog Feature Flags using the
 *               client-side RUM credentials.
 */
export const createWebFeatureFlagProvider = (
  config: WebProviderConfig
): Provider => {
  switch (config.kind) {
    case 'flagd':
      return new FlagdWebProvider({
        host: config.host,
        port: config.port,
        tls: config.tls,
      });
    case 'datadog':
      return new DatadogProvider({
        clientToken: config.clientToken,
        applicationId: config.applicationId,
        site: config.site,
        env: config.env,
        service: config.service,
        version: config.version,
        // Routes the flag-configuration fetch through our own origin when set,
        // replacing the ad-blocked `preview.ff-cdn.datadoghq.eu` host. Only the
        // config fetch — evaluation telemetry keeps going direct to intake.
        flaggingProxy: config.flaggingProxy,
        // Exposure logging emits one RUM event per flag evaluation — keep it
        // off until we deliberately opt in to that cost.
        enableExposureLogging: false,
      });
    case 'in-memory':
      return new WebInMemoryFeatureFlagProvider();
  }
};
